// Client for the Chrome-extension distribution endpoints.
//
//   GET  /extension/info     — { version, size_bytes, released_at, available }
//   GET  /extension/download — streams the zip; we fetch as a blob and trigger
//                              a browser download (the response sets
//                              Content-Disposition, but downloading blobs
//                              from a same-origin fetch is the most reliable
//                              cross-browser pattern — mirrors
//                              src/api/projects.js#downloadFile).

import { api, filenameFromDisposition, getApiBase, getToken, qs } from "./client";
import { detectBrowser } from "@/lib/browser";

export function getExtensionInfo() {
  return api.get("/extension/info");
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export async function downloadExtension() {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + "/extension/download", origin);
  const token = getToken();

  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let detail = `Download failed (${res.status})`;
    try {
      const data = await res.json();
      if (data && data.detail) detail = data.detail;
    } catch (e) {
      /* not JSON — use the status-based message */
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const dispo = res.headers.get("Content-Disposition") || "";
  const filename = filenameFromDisposition(dispo, "ai-qa-engine-extension.zip");

  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);

  return { filename, sizeBytes: blob.size, sizeLabel: formatSize(blob.size) };
}

// ===========================================================================
//  HEADED extension flow (the /extension run package — separate tables).
//  Shared by the side panel (executes runs) and the dashboard Extension section
//  (commands a connected device + watches results). Never touches /test.
// ===========================================================================

// ---- runs ----------------------------------------------------------------
// NOT stamped with detectBrowser() here: a dashboard-initiated run executes on a
// remote device, so this tab's browser is not the one that will run it. The
// EXTENSION passes its own browser explicitly (see ExtensionPanel); a dashboard
// run leaves it null, which the UI shows as "—" rather than as this tab's browser.
export const startExtRun = (payload) => api.post("/extension/run", payload);
export const getExtRun = (id) => api.get(`/extension/runs/${id}`);
// mine=true scopes to the caller's OWN runs (not the whole company) — used by
// the tester Runs & Bugs page so a tester sees only the cases they tested.
// standalone=true drops runs that belong to a batch item, leaving the single
// runs — the batch-first History / Runs & Bugs screens list those separately.
export const listExtRuns = (limit = 50, { mine = false, standalone = false } = {}) =>
  api.get("/extension/runs", {
    params: { limit, mine: mine || undefined, standalone: standalone || undefined },
  });
// History → Delete all: remove every run in the caller's scope.
export const deleteAllExtRuns = () => api.del("/extension/runs");
export const ingestExtRun = (id, payload) =>
  api.post(`/extension/runs/${id}/ingest`, payload);

// Per-step screenshot from the real tab (data URL). Stored on the backend and
// served via GET /extension/runs/{id}/screenshots/{step} for the detail view.
export const uploadExtScreenshot = (id, stepIndex, image) =>
  api.post(`/extension/runs/${id}/screenshot`, { step_index: stepIndex, image });

// ---- the agentic brain (one action at a time) ----------------------------
export const nextAction = (payload) =>
  api.post("/extension/agent/next-action", payload);

// ---- one-shot whole-case plan (fast Batch #1) -----------------------------
export const planCase = (payload) =>
  api.post("/extension/agent/plan-case", payload);

// ---- vision review (visual/UI bugs the DOM can't see) ---------------------
export const reviewPage = (payload) =>
  api.post("/extension/agent/review", payload);

// ---- devices -------------------------------------------------------------
export const listDevices = () => api.get("/extension/devices");

// ---- deterministic tests -------------------------------------------------
export const listExtTests = () => api.get("/extension/tests");
export const getExtTest = (id) => api.get(`/extension/tests/${id}`);
// Saved Tests → Delete all: remove every saved (compiled) test in the caller's scope.
export const deleteAllExtTests = () => api.del("/extension/tests");
export const compileRun = (runId, name) =>
  api.post(`/extension/runs/${runId}/compile`, { name });
export const replayTest = (testId, deviceId) =>
  api.post(`/extension/tests/${testId}/replay`, null, {
    params: { device_id: deviceId },
  });

export const isTerminalExt = (status) =>
  ["passed", "failed", "error", "cancelled", "inconclusive", "blocked"].includes(
    String(status || "").toLowerCase()
  );

// ===========================================================================
//  BATCH DISTRIBUTION — import cases, split 10-at-a-time across chosen
//  extensions, live control. Mirrors the exe's /exe/* wrappers.
// ===========================================================================

// ---- import test cases ---------------------------------------------------
export const detectExtColumns = (columns, samples) =>
  api.post("/extension/test-cases/detect-columns", { columns, samples });
export const importExtCases = (payload) =>
  api.post("/extension/test-cases/import", payload);
export const moveExtCases = (caseIds, projectId) =>
  api.post("/extension/test-cases/move", { case_ids: caseIds, project_id: projectId || null });
export const listExtCases = (limit = 500) =>
  api.get("/extension/test-cases", { params: { limit } });
export const deleteExtCase = (id) => api.del(`/extension/test-cases/${id}`);
// Import Cases → Delete all: remove every imported case in the caller's scope.
export const deleteAllExtCases = () => api.del("/extension/test-cases");

// ---- batches -------------------------------------------------------------
export const createExtBatch = (payload) => api.post("/extension/batches", payload);
// projectId narrows the query server-side. Fetching the newest N company-wide and
// filtering by project in the browser drops a quiet project's batches entirely.
// mine=true scopes to the caller's OWN batches (not the whole company /
// every assigned project) — the tester Runs & Bugs page uses it so its
// totals match the per-user figures on the home dashboard.
export const listExtBatches = (limit = 50, { projectId, mine = false } = {}) =>
  api.get("/extension/batches", {
    params: { limit, project_id: projectId || undefined, mine: mine || undefined },
  });
export const getExtBatch = (id) => api.get(`/extension/batches/${id}`);
// The endpoint now pages and returns {items, total, limit, offset}. This keeps
// returning a plain array so the existing screens are unaffected; use
// pageExtBatchItems when you need the total to page against.
export const getExtBatchItems = (id) =>
  api.get(`/extension/batches/${id}/items`).then((r) => (Array.isArray(r) ? r : r?.items || []));
export const pageExtBatchItems = (id, params = {}) =>
  api.get(`/extension/batches/${id}/items${qs(params)}`);
export const getExtCompileStatus = (id) =>
  api.get(`/extension/batches/${id}/compile-status`);
export const getExtBatchLogin = (id) => api.get(`/extension/batches/${id}/login`);
export const pauseExtBatch = (id) => api.post(`/extension/batches/${id}/pause`);
export const resumeExtBatch = (id) => api.post(`/extension/batches/${id}/resume`);
export const stopExtBatch = (id) => api.post(`/extension/batches/${id}/stop`);
export const takeoverExtBatch = (id) => api.post(`/extension/batches/${id}/takeover`);
export const recompileExtBatch = (id) => api.post(`/extension/batches/${id}/recompile`);
// Download the XLSX execution report for a batch (auth'd fetch → blob → browser download).
export async function downloadExtBatchReport(id) {
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + `/extension/batches/${id}/report`, origin);
  const token = getToken();
  const res = await fetch(url.toString(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`Report download failed (${res.status})`);
  const blob = await res.blob();
  const dispo = res.headers.get("Content-Disposition") || "";
  const filename = filenameFromDisposition(dispo, "batch_report.xlsx");
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(objUrl);
}
// Per-item triage: requeue a terminal item for another attempt, or cancel a
// still-active one. Both return the updated item; refetch items after calling.
export const retryExtItem = (batchId, itemId) =>
  api.post(`/extension/batches/${batchId}/items/${itemId}/retry`);
export const cancelExtItem = (batchId, itemId) =>
  api.post(`/extension/batches/${batchId}/items/${itemId}/cancel`);
// Running → Delete all: stop any live batches, then remove every batch in scope.
export const deleteAllExtBatches = () => api.del("/extension/batches");
// Clear all data: batches + runs + imported cases in the caller's scope.
export const clearAllExtData = () => api.del("/extension/data");

// ---- the extension's own claim-loop back-channel (used by batchRunner.js) -
export const claimExtItems = (batchId, deviceId, max = 10) =>
  api.post(`/extension/batches/${batchId}/claim`, { device_id: deviceId, max });
// Safe to stamp: this back-channel is only ever called BY the extension, so the
// browser detected here is the one actually executing the case.
export const startExtItem = (batchId, itemId, deviceId) =>
  api.post(`/extension/batches/${batchId}/items/${itemId}/start`,
    { device_id: deviceId, browser: detectBrowser() });
export const heartbeatExtItem = (batchId, itemId, runId) =>
  api.post(`/extension/batches/${batchId}/items/${itemId}/heartbeat`, { run_id: runId });
export const completeExtItem = (batchId, itemId, payload) =>
  api.post(`/extension/batches/${batchId}/items/${itemId}/complete`, payload);
export const pendingExtBatch = (deviceId) =>
  api.get("/extension/agent/pending-batch", { params: { device_id: deviceId } });

// ---- git repo (Compile Phase source) -------------------------------------
// Lightweight "is any repo connected?" badge check (kept for back-compat).
export const getExtRepoConfig = () => api.get("/extension/repo-config");
// Multiple saved repos (one per project).
export const listExtRepoConfigs = () => api.get("/extension/repo-configs");
export const createExtRepoConfig = (payload) => api.post("/extension/repo-configs", payload);
export const updateExtRepoConfig = (id, payload) => api.post(`/extension/repo-configs/${id}`, payload);
export const testExtRepoConfig = (payload) => api.post("/extension/repo-configs/test", payload);
export const deleteExtRepoConfig = (id) => api.del(`/extension/repo-configs/${id}`);

// ---- Sample-File Kit (smart uploads) -------------------------------------
export const listExtSampleFiles = () => api.get("/extension/sample-files");
export const uploadExtSampleFile = (payload) => api.post("/extension/sample-files", payload);
export const getExtSampleContent = (fileType) =>
  api.get(`/extension/sample-files/${encodeURIComponent(fileType)}/content`);
export const deleteExtSampleFile = (id) => api.del(`/extension/sample-files/${id}`);

// Build the WebSocket URL the extension uses to register + receive commands.
// Derives ws(s):// from the configured API base (absolute in the extension).
export function extWsUrl(token, deviceId) {
  let base = getApiBase();
  if (!/^https?:\/\//.test(base)) {
    base = typeof window !== "undefined" ? window.location.origin : "";
  }
  const wsBase = base.replace(/^http/, "ws");
  const params = new URLSearchParams();
  if (token) params.set("token", token);
  if (deviceId) params.set("device_id", deviceId);
  return `${wsBase}/extension/agent/socket?${params.toString()}`;
}
// ---- Re-run (dev-fix verification cycle) ---------------------------------
// Re-runs ONLY what needs it: cases a developer claims to have fixed (to verify)
// plus the ones still failing. Already-passed untouched cases are skipped, which
// is where the time and token savings come from.
export const getExtRerunPreview = (id) => api.get(`/extension/batches/${id}/rerun-preview`);
export const rerunExtBatch = (id) => api.post(`/extension/batches/${id}/rerun`, {});
